import { EntityTypeIdentifier } from '@type/entity-type-identifier'
import { EntityTypeConfigService } from '@service/entity-type-config.service'
import { EntityTypeConfig } from '@model/entity-type-config'
import { OnInit, Component, ViewChild } from '@angular/core'
import { ScrollService } from '@service/scroll.service'
import { FormGroup } from '@angular/forms'
import { MatStepper } from '@angular/material/stepper'
import { StepperSelectionEvent } from '@angular/cdk/stepper'
import { UserService } from '@service/user/user.service'
import { Entity } from '@model/entities/entity'
import { modelToFormGroup } from 'aautil'
import { tinyDefaultConfig } from '@configuration/tiny-default-config'
import { appConfig } from '@configuration/app-config'
import { MatDialog } from '@angular/material/dialog'
import { TransitionHookService } from '@service/transition-hook.service'
import { ConfirmDialogComponent } from '@modal/confirm-dialog/confirm-dialog.component'
import { ToastService } from '@service/toast/toast.service'
import { CategoryType } from '@enum/category-type'
import { LoadingService } from '@service/loading.service'
import { HelperService } from '@service/helper.service'
import { environment } from '@environments/environment'
import { ImageKeys } from '@type/image-keys'
import { StateService } from '@service/state.service'
import { ActivatedRoute, Router } from '@angular/router'

@Component({
  template: ''
})
export abstract class EntityCreateComponent implements OnInit {

  /*
    we have to maintain two separate states of the data
    one in the form
    one in the draft
    which needs synchronising effort and code etc. 

    because not all attributes of the entity/draft are always represented in each form
  */

  protected abstract entityTypeIdentifier: EntityTypeIdentifier

  protected entityTypeConfig: EntityTypeConfig = null

  public form: FormGroup = null
  public formSubmitted: boolean = false

  public abstract draft: Entity

  // tinymce
  public tinyDefaultConfig = tinyDefaultConfig

  public appConfig = appConfig

  // offer it to template
  public CategoryType = CategoryType

  /* do we edit a post or create a new one */
  public edit: boolean = false

  @ViewChild('stepper')
  set stepper(stepper: MatStepper) {

    if (stepper == null)
      return

    this._stepper = stepper

    // subscribe to mat step change
    this.stepper.selectionChange.subscribe((stepSelectionChange: StepperSelectionEvent) => {
      this.currentStepLabel = stepSelectionChange.selectedStep.label
    })

  }
  get stepper(): MatStepper {
    return this._stepper
  }
  private _stepper: MatStepper = null
  public currentStepLabel: string = ''

  public stepsCompleted: number = 0

  private extras

  constructor(
    public entityTypeConfigService: EntityTypeConfigService,
    protected ScrollService: ScrollService,
    public userService: UserService,
    protected LoadingService: LoadingService,
    protected HelperService: HelperService,
    protected transitionHookService: TransitionHookService,
    protected dialog: MatDialog,
    protected toastService: ToastService,
    public StateService: StateService,
    public ActivatedRoute: ActivatedRoute,
    public Router: Router
  ) {
    this.ActivatedRoute.params.subscribe(params => {
      this.StateService.params = params
    })

    this.extras = this.Router.getCurrentNavigation().extras.state
  }

  public ngOnInit() {

    this.entityTypeConfig = this.entityTypeConfigService.getConfigByName(this.entityTypeIdentifier)
    this.ScrollService.scrollToTop()

    this.LoadingService.startLoad()

    // first check if its an edit of an entity or the creation of a new one
    this.isEdit()

      // its an edit -> init form with existing data -> done
      .then((entity: Entity) => {
        this.edit = true
        this.draft = entity
        this.endInit()
      })

      // its a create
      .catch(() => {

        this.edit = false

        // check if the user created before (a draft exists)
        this.entityTypeConfig.service.createDraftIfNotExists()
          .then((response) => {

            // if no draft exists its a new one -> init an empty entity and form
            if (response != 'DRAFT_FOR_USER_ALREADY_EXISTS') {
              this.draft = response

              this.usePreselected()
              this.endInit()
            }

            // draft exists already -> ask user if he wants to throw or use it
            else {
              this.LoadingService.stopLoad()

              this.dialog.open(ConfirmDialogComponent, {
                data: {
                  text: `Beim vorherigen Verlassen der Webseite wurde dein angefangener Beitrag als Entwurf gespeichert.
                Willst Du einen neuen Beitrag erstellen oder den Entwurf wieder benutzen?`,
                  buttons: [
                    {
                      text: 'Neuer Beitrag',
                      value: 'abort',
                    },
                    {
                      text: 'Entwurf benutzen',
                      value: 'keep',
                    },
                  ],
                },
                disableClose: true,
                panelClass: ConfirmDialogComponent.cssClass
              })
                .afterClosed().subscribe(decision => {
                  this.LoadingService.startLoad()

                  if (decision == 'abort') {
                    this.entityTypeConfig.service.discardDraft()
                      .then((draft: Entity) => {
                        this.draft = draft
                        this.usePreselected()
                        this.endInit()
                      })
                  }
                  else if (decision == 'keep') {
                    this.entityTypeConfig.service.getDraft()
                      .then(draft => {
                        this.draft = draft
                        this.endInit()
                      })
                  }
                })
            }

          })
          .catch(err => {
            this.toastService.showError('Der Entwurf konnte nicht angelegt werden, bitte versuchen Sie es erneut.')
          })

      })
  }

  // do this always after everything is set
  private endInit() {
    this.initFormGroup()
    this.transitionHookService.startBeforeLeaveHook()
    this.customAfterOnInit()
    this.LoadingService.stopLoad()
  }

  // this is a stub to be overwritten by inheriting components if necessary
  protected customAfterOnInit() { }

  // this is a stub to be overwritten by inheriting components if necessary
  protected customPreUpdate() { }

  // this is a stub to be overwritten by inheriting components if necessary
  protected customPostUpdate() { }

  private isEdit(): Promise<Entity> {
    return new Promise((resolve, reject) => {

      // edit
      if (window.location.pathname.indexOf("/edit/") != -1) {

        if (this.StateService.params && typeof this.StateService.params.id != 'undefined') {
          this.entityTypeConfig.service.editDraft(parseInt(this.StateService.params.id))
            .then((entity: Entity) => {
              resolve(entity)
            })
            .catch(e => {
              console.log("I WAS REJECTED", e)

              if (e.errors.includes("NOT YOURS!")) {
                this.toastService.showError("Es scheint der Beitrag gehört dir nicht. Sollte das ein Fehler sein kontaktiere uns bitte mit den Details. Danke für deine Hilfe!")

                setTimeout(() => {
                  this.StateService.goto("/news")
                }, 4000);
              }
            })
        }
      }
      // create
      else {
        reject()
      }
    })
  }

  /*
    in the create menus / btns its possible to "pre-choose" some attributes of the new entity
    for example click a button that says "sell stuff" it has to has the possibility to pre fill the new entity
    here we respect that
  */
  private usePreselected() {
    if (this.extras) {

      if (typeof this.extras["preselected"] != "undefined" && this.extras["preselected"]) {

        for (const k of Object.keys(this.extras["preselected"])) {
          this.draft[k] = this.extras["preselected"][k]
        }
      }
    }
  }

  public goBackToList() {
    this.StateService.goto(
      this.entityTypeConfig.liststate
    )
  }

  public goToCreate() {

    // if we are already in the create state -> reload it
    if (window.location.pathname.indexOf(this.entityTypeConfig.createstate) != -1) {
      const currentUrl = this.Router.url
      this.Router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.Router.navigate([currentUrl])
      })

      return
    }

    this.StateService.goto(this.entityTypeConfig.createstate)
    this.ScrollService.scrollToTop()
  }

  /*
    this method is executed each time one tries to advance the matstepper in the creation process

    @param shadowUpdate
    This allows to define if the result from the backend update arre mirrored back to the frontend.
    Standard it is treated as "fire and forget" / "shadowupdate"

    @param fieldsToControl
    Allows to specify which fields of the form are to check for validity.
    This allows to show only parts of the form i.e. in our create steppers.
  */
  public nextIfFormValid(shadowUpdate: boolean = true, fieldsToControl?: string[]) {

    this.formSubmitted = true

    // determine if we should go to the next mat-step
    if (fieldsToControl) {
      for (let field of fieldsToControl) {
        if (this.form.get(field).invalid) {
          return;
        }
      }
    } else {
      if (!this.form.valid) {
        return;
      }
    }

    this.stepsCompleted++

    this.LoadingService.startLoad()
    this.updateDraft(shadowUpdate)
      .then(() => {
        this.stepper.next()
        this.ScrollService.scrollToTop()
      })
      .finally(() => {
        this.LoadingService.stopLoad()
        this.formSubmitted = false
      })
  }

  public publish() {
    this.LoadingService.startLoad()

    this.stepsCompleted++

    this.entityTypeConfig.service.publishDraft(this.draft)
      .then((draft: Entity) => {
        this.draft = draft
        this.stepper.next()
      })
      .catch(err => {
        if (err && err.error && err.error.errors && err.error.errors.messages[0] == 'NOT_ENOUGH_CREDITS') {
          this.toastService.showError('Deine Credits reichen leider nicht aus.')
        } else {
          this.toastService.showError('Es ist ein Fehler aufgetreten, bitte versuchen Sie es erneut.')
        }
        this.stepsCompleted--
      })
      .finally(() => {
        this.ScrollService.scrollToTop()
        this.LoadingService.stopLoad()
        this.transitionHookService.endBeforeLeaveHook()
      })
  }

  /*
    this is the main "backup" method
    it updates values on input blur in the backend so one can resume the edit later if one reloads etc.
    
    if its a shadow update the results wont be used for anything

    sometimes its being executed two times - i.e. if you are focused in an input and click "next"
    then on blur and next button fire simultaneously
  */
  public updateDraft(shadowUpdate: boolean = true): Promise<void> {
    return new Promise((resolve, reject) => {

      this.customPreUpdate()

      // this needs to be done whenever something in the form changes
      // for convenience we put it here because whenever something in the form changes we execute updateDraft anyhow
      this.calcStateVars()

      let draft = this.form.value
      draft["images"] = this.draft["images"]

      this.entityTypeConfig.service.updateDraft(draft)
        .then((draft) => {

          if (!shadowUpdate) {
            this.draft = draft
            this.initFormGroup()
            this.customPostUpdate()
          }

          resolve()
        })

    })
  }

  /*
    this is the method that is called when a file is selected via the browser native file picker

    target_id needs to be optionally overwritable for wiki which will send the id of a content part instead the draft
  */
  public fileSelected(event: any, purpose: any, target_id: number = this.draft.id) {
    if (event.target.files && event.target.files[0]) {
      this.fileDropped(event.target.files, purpose, target_id)
    }
  }

  /*
    this is executed when the user drag & drops a file
    also it is used in fileSelected to reduce code redundancy
  */
  public fileDropped(filelist: any, purpose: ImageKeys, target_id: number = this.draft.id) {

    /*
      update the draft before you upload the image
      the browser does no blur when you've written something and click in the explorer
    */
    this.updateDraft()
      .then(() => {

        // now start the real image upload
        this.LoadingService.startLoad()

        // if its the main image -> delete the old
        if (purpose.includes('MAIN') && this.draft.mainImage && !this.draft.mainImage.isFallback) {
          this.entityTypeConfig.service.deleteImage(
            this.draft.id,
            this.draft.mainImage.image.id
          )
            .then(() => {
              this.callServiceFileDropped(filelist, purpose, target_id)
            })
        }
        else {
          this.callServiceFileDropped(filelist, purpose, target_id)
        }
      })
  }

  /*
    this finally calls the final entity service to really upload the image over the wire
  */
  public callServiceFileDropped(filelist: any, purpose: string, target_id: number) {

    /*
      the "any" here is really bad practice
      but I didn't want to change the basics of the class hierarchy a day before golive
      this should be solved with generics in the future

      EntityCreateComponent<T>
    */

    this.LoadingService.startLoad()
    this.entityTypeConfig.service.onDropFile<any>(
      filelist,
      purpose,
      target_id
    )
      .then((draft: any) => {
        this.draft = draft
        this.initFormGroup()
      })
      .finally(() => {
        this.LoadingService.stopLoad()
      })
  }

  /*
    this function is meant to calculate information needed after each entity update
  */
  protected abstract calcStateVars()

  /*
    this method is intended to be called every time the form is set
  */
  protected initFormGroup() {

    if (!environment.production) {
      console.log("initFormGroup")
    }

    this.form = modelToFormGroup(this.draft)
    this.calcStateVars()

    /*
      some actions need to be done only if its an edit
      i.e. disabling profile vs company_profile selection - because right now the system does not support changing that

      also they need to be definable by the implementing child class because they can differ
    */
    if (this.draft.is_edit) {
      this.customEditActions()
    }
  }

  public abstract customEditActions()
}